import React from 'react';
import styles from './GeneralFilter.module.css';
import { ChevronDown } from 'lucide-react';

interface FilterOption {
  value: string;
  label: string;
}

interface GeneralFilterProps {
  label?: string;
  labelInSelect?: string; // prefix text shown only above select (not inside dropdown options)
  value: string | string[];
  options: FilterOption[];
  onChange: (value: string | string[]) => void;
  className?: string;
  height?: string | number;
  width?: string | number;
  closeOnSelect?: boolean; // NEW: control whether dropdown closes after selecting
  multiSelect?: boolean; // NEW: enable multi-select mode
}

const GeneralFilter: React.FC<GeneralFilterProps> = ({ label, labelInSelect, value, options, onChange, className, height, width, closeOnSelect = true, multiSelect = false }) => {
  const [open, setOpen] = React.useState(false)
  const [activeIdx, setActiveIdx] = React.useState(-1)
  const triggerRef = React.useRef<HTMLDivElement | null>(null)
  const listRef = React.useRef<HTMLDivElement | null>(null)
  const selectedValues = Array.isArray(value) ? value : [value]
  const selected = multiSelect
    ? options.filter(o => selectedValues.includes(o.value))
    : options.find(o => o.value === value)

  // Close when clicking outside
  React.useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (triggerRef.current && triggerRef.current.contains(e.target as Node)) return
      if (listRef.current && listRef.current.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (["ArrowDown", "Enter", " "].includes(e.key)) {
        e.preventDefault(); setOpen(true); setActiveIdx(options.findIndex(o => selectedValues.includes(o.value)))
      }
      return
    }
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); return }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, options.length - 1)); return }
    if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); return }
    if (e.key === "Home") { e.preventDefault(); setActiveIdx(0); return }
    if (e.key === "End") { e.preventDefault(); setActiveIdx(options.length - 1); return }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (activeIdx >= 0 && options[activeIdx]) {
        if (multiSelect) {
          const optValue = options[activeIdx].value;
          let newSelected: string[];
          if (selectedValues.includes(optValue)) {
            newSelected = selectedValues.filter(v => v !== optValue);
          } else {
            newSelected = [...selectedValues, optValue];
          }
          onChange(newSelected);
        } else {
          onChange(options[activeIdx].value);
          if (closeOnSelect) setOpen(false);
        }
      }
    }
  }

  return (
    <div className={styles.filterWrapper + (className ? ' ' + className : '')}>
      {label && <label className={styles.filterLabel}>{label}</label>}
      <div className={styles.customSelectOuter}>
        <div
          ref={triggerRef}
          className={styles.customSelect + (open ? ' ' + styles.open : '')}
          tabIndex={0}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? `${label || 'filter'}-listbox` : undefined}
          onClick={() => setOpen(o => !o)}
          onKeyDown={handleKeyDown}
          style={height || width ? { height, width } : {}}
        >
          <span className={styles.valueText}>
            {multiSelect
              ? selected && Array.isArray(selected) && selected.length > 0
                ? selected.map(s => s.label).join(', ')
                : labelInSelect || ''
              : labelInSelect && selected && !Array.isArray(selected) ? `${labelInSelect} ${selected.label}` : (!Array.isArray(selected) && selected ? selected.label : '')}
          </span>
          <span className={styles.chevron} aria-hidden="true"><ChevronDown /></span>
        </div>
        {open && (
          <div
            ref={listRef}
            role="listbox"
            id={`${label || 'filter'}-listbox`}
            className={styles.optionsPanel}
            tabIndex={-1}
          >
            {options.map((opt, idx) => {
              const active = idx === activeIdx
              const selectedOpt = multiSelect
                ? selectedValues.includes(opt.value)
                : opt.value === value
              return (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={selectedOpt}
                  className={styles.option + (active ? ' ' + styles.optionActive : '') + (selectedOpt ? ' ' + styles.optionSelected : '')}
                  onMouseEnter={() => setActiveIdx(idx)}
                  onMouseDown={e => {
                    e.preventDefault();
                    if (multiSelect) {
                      let newSelected: string[];
                      if (selectedValues.includes(opt.value)) {
                        newSelected = selectedValues.filter(v => v !== opt.value);
                      } else {
                        newSelected = [...selectedValues, opt.value];
                      }
                      onChange(newSelected);
                    } else {
                      onChange(opt.value);
                      if (closeOnSelect) setOpen(false);
                    }
                  }}
                >
                  <span>{opt.label}</span>
                  {selectedOpt && <span className={styles.checkMark}>✓</span>}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
};

export default GeneralFilter;
